import type { Vec2, WallGrid, WallMetrics } from '../types';
import { tessellateGrid } from './tessellate';
import type { ShapeDefinition } from './registry';

/**
 * The square panel — the Phase 1 shape.
 *
 * `size` is the edge length in millimetres, and panels tile on a simple
 * `size + gap` pitch in both directions. A square overrides the default
 * tessellation because a regular grid beats a centroid fan here: even triangle
 * sizes, fewer vertices and no fan singularity in the middle of the panel.
 */
export const squareShape: ShapeDefinition = {
  id: 'square',
  name: 'Square',
  description: 'Edge-to-edge square panels on a regular grid.',
  phase: 1,

  outline(size: number): Vec2[] {
    const half = size / 2;
    return [
      { x: -half, y: -half },
      { x: half, y: -half },
      { x: half, y: half },
      { x: -half, y: half },
    ];
  },

  tessellate(size: number, _gap: number, segments: number) {
    return tessellateGrid(size, size, segments);
  },

  layout(column: number, row: number, size: number, gap: number): Vec2 {
    const pitch = size + gap;
    return { x: size / 2 + column * pitch, y: size / 2 + row * pitch };
  },

  metrics(grid: WallGrid, size: number, gap: number): WallMetrics {
    const pitch = size + gap;
    return {
      columns: grid.columns,
      rows: grid.rows,
      width: grid.columns * size + Math.max(0, grid.columns - 1) * gap,
      height: grid.rows * size + Math.max(0, grid.rows - 1) * gap,
      pitchX: pitch,
      pitchY: pitch,
      panelSize: size,
    };
  },
};
